import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Button, Divider, Typography } from "@mui/material";
import { BookingTypeEnum, InvestmentTypeEnum } from "@api";
import { StepContainer } from "../StepContainer";
import { BookingTypeWithSplit } from "../stateMachine/types/splitValues";

const Container = styled.div`
  width: 100%;
`;

export const StyledDivider = styled(Divider)`
  margin-top: ${({ theme }) => theme.spacing(6)};
  margin-bottom: ${({ theme }) => theme.spacing(6)};
  border: 1;
  border-style: "solid";
  border-color: ${({ theme }) => theme.palette.divider};
`;

const FieldWithInfoContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

export interface Props {
  handleNext: (bookingType: BookingTypeWithSplit) => void;
  handlePrevious?: () => void;
  investmentType: InvestmentTypeEnum;
  defaultValue?: BookingTypeEnum;
  isNewInvestment: boolean;
}

export const BookingTypeStep = ({
  handleNext,
  handlePrevious,
  investmentType,
  defaultValue,
  isNewInvestment,
}: Props) => {
  const { t } = useTranslation();
  const [value, setValue] = useState<BookingTypeWithSplit | undefined>(
    defaultValue
  );
  const handleClick = (bookingType: BookingTypeWithSplit) => {
    setValue(bookingType);
    handleNext(bookingType);
  };

  const mostUsed: BookingTypeEnum[] = ["buy", "sell"];
  const specials: BookingTypeWithSplit[] = [
    "book_in",
    "book_out",
    "switch",
    "retained_dividend",
    "carve_out",
    "split",
  ];
  const disabledForNewInvestment: Set<string> = new Set<string>(
    isNewInvestment ? ["sell", "book_out", "switch", "carve_out", "split"] : []
  );

  return (
    <StepContainer
      testid="booking-type-step"
      title={t("transactionWizard.bookingTypeStep.title")}
      onPrevious={handlePrevious}
    >
      <Container>
        <Typography variant="h6" mb={6}>
          {t("transactionWizard.bookingTypeStep.mostUsed")}
        </Typography>
        {mostUsed
          .filter((mu) => !disabledForNewInvestment.has(mu))
          .map((bookingType) => {
            return (
              <FieldWithInfoContainer key={bookingType}>
                <Button
                  variant={value === bookingType ? "secondary" : "primary"}
                  data-testid={`${bookingType}-button`}
                  disabled={disabledForNewInvestment.has(bookingType)}
                  onClick={() => handleClick(bookingType)}
                >
                  {t(`transactionWizard.bookingTypeStep.${bookingType}`)}
                </Button>
              </FieldWithInfoContainer>
            );
          })}

        {investmentType !== "91_managed" && (
          <>
            <StyledDivider />

            <Typography variant="h6" mb={6}>
              {t("transactionWizard.bookingTypeStep.specials")}
            </Typography>
            {specials
              .filter((mu) => !disabledForNewInvestment.has(mu))
              .map((bookingType) => {
                return (
                  <FieldWithInfoContainer key={bookingType}>
                    <Button
                      variant={value === bookingType ? "secondary" : "primary"}
                      data-testid={`${bookingType}-button`}
                      disabled={disabledForNewInvestment.has(bookingType)}
                      onClick={() => handleClick(bookingType)}
                    >
                      {t(`transactionWizard.bookingTypeStep.${bookingType}`)}
                    </Button>
                  </FieldWithInfoContainer>
                );
              })}
          </>
        )}
      </Container>
    </StepContainer>
  );
};
